import { cn } from '@listening/utils/react-helpers';
import { useEffect, useState } from 'react';

type SlideData = {
  id: number;
  title: string;
  subtitle: string;
  backgroundGradient: string;
  numberGradient: string;
  problem: string;
  impact: string;
  solution: string;
  solutionDetail: string;
};

type SeoSliderProps = {
  slides: Array<SlideData>;
  title?: string | undefined;
  description?: string | undefined;
  autoplayDelay?: number | undefined;
  className?: string | undefined;
};

export function SeoSlider({
  slides,
  title = 'The Scientific Pronunciation Challenge',
  description = 'Discover how pronunciation accuracy transforms professional communication',
  autoplayDelay = 8000,
  className,
}: SeoSliderProps) {
  const [currentSlide, setCurrentSlide] = useState(0);

  // Auto-advance slides
  useEffect(() => {
    if (autoplayDelay <= 0) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, autoplayDelay);

    return () => {
      clearInterval(interval);
    };
  }, [autoplayDelay, slides.length]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  return (
    <div
      className={cn(
        'mx-auto w-full max-w-[1600px] px-4 sm:px-6 lg:px-8',
        className,
      )}
    >
      <div className='mb-8 text-center sm:mb-12'>
        <h2 className='mb-3 text-2xl font-bold text-blue-600 sm:mb-4 sm:text-3xl md:text-4xl'>
          {title}
        </h2>
        <p className='mx-auto max-w-xl px-4 text-base text-gray-600 sm:max-w-2xl sm:px-0 sm:text-lg'>
          {description}
        </p>
      </div>

      <div className='relative overflow-hidden rounded-xl sm:rounded-2xl'>
        <div
          className='flex transition-transform duration-500 ease-in-out'
          style={{ transform: `translateX(-${currentSlide.toString()}00%)` }}
        >
          {slides.map((slide) => (
            <div className='w-full shrink-0 p-4 sm:p-6 lg:p-8' key={slide.id}>
              <div className='relative rounded-xl border border-gray-200 bg-white p-4 shadow-lg backdrop-blur-sm sm:rounded-2xl sm:p-6 lg:p-8'>
                <div className='mb-4 flex flex-col sm:mb-6 sm:flex-row sm:items-center'>
                  <div
                    className={cn(
                      'mb-3 flex h-10 w-10 items-center justify-center self-start rounded-lg bg-linear-to-r sm:mr-4 sm:mb-0 sm:h-12 sm:w-12 sm:self-center sm:rounded-xl',
                      slide.numberGradient,
                    )}
                  >
                    <span className='text-lg font-bold text-white sm:text-xl'>
                      {slide.id}
                    </span>
                  </div>
                  <div className='flex-1'>
                    <h3 className='mb-1 text-lg font-bold text-gray-900 sm:mb-0 sm:text-xl lg:text-2xl'>
                      {slide.title}
                    </h3>
                    <p className='text-sm font-semibold text-blue-600 sm:text-base lg:text-lg'>
                      {slide.subtitle}
                    </p>
                  </div>
                </div>

                <div className='grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-2 lg:gap-8'>
                  <div className='order-2 space-y-4 sm:space-y-6 lg:order-1'>
                    <div className='rounded-lg border-l-4 border-red-400 bg-red-50 p-4 sm:p-6'>
                      <h4 className='mb-2 text-sm font-bold text-red-900 sm:text-base'>
                        Problem
                      </h4>
                      <p className='text-sm leading-relaxed text-red-800 sm:text-base'>
                        {slide.problem}
                      </p>
                    </div>

                    <div className='rounded-lg border-l-4 border-orange-400 bg-orange-50 p-4 sm:p-6'>
                      <h4 className='mb-2 text-sm font-bold text-orange-900 sm:text-base'>
                        Impact
                      </h4>
                      <p className='text-sm leading-relaxed text-orange-800 sm:text-base'>
                        {slide.impact}
                      </p>
                    </div>
                  </div>

                  <div className='order-1 rounded-lg border-l-4 border-green-400 bg-green-50 p-4 sm:p-6 lg:order-2'>
                    <h4 className='mb-2 text-sm font-bold text-green-900 sm:text-base'>
                      Our Solution
                    </h4>
                    <p className='mb-2 text-base font-semibold text-green-800 sm:text-lg'>
                      {slide.solution}
                    </p>
                    <p className='text-sm leading-relaxed text-green-700 sm:text-base'>
                      {slide.solutionDetail}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className='mt-6 flex justify-center space-x-2 sm:mt-8 sm:space-x-3'>
        {slides.map((slide, index) => (
          <button
            aria-label={`Go to slide ${(index + 1).toString()}`}
            className={cn(
              'h-2.5 w-2.5 rounded-full transition-all duration-300 sm:h-3 sm:w-3',
              currentSlide === index
                ? 'bg-blue-600'
                : 'bg-gray-300 hover:bg-gray-400',
            )}
            key={slide.id}
            onClick={() => {
              goToSlide(index);
            }}
            type='button'
          />
        ))}
      </div>
    </div>
  );
}
